/**
 * BorradorDeEdicionDeEntidadConSnapshotOriginal
 *
 * QUÉ:  estructura de datos en RAM que sostiene una copia editable de la
 *       configuración de una entidad de admin (sorteo, nodo, pozo,
 *       dispositivo) mientras el usuario está editándola en un modal.
 *
 * DÓNDE: vive en memoria del modal admin desde el momento en que se abre
 *        hasta que se guarda o descarta. No persiste en localStorage,
 *        no sale por red hasta el "guardar".
 *
 * CÓMO:  al crearse hace deepClone del snapshot original recibido del
 *        backend. El snapshot original queda intacto, congelado. Toda
 *        edición se aplica sobre la copia de trabajo. Al guardar el caller
 *        envía la copia completa al backend, que hace deepMerge sobre lo
 *        que YA tiene en DB (campo ausente preserva, null borra explícito).
 *
 * POR QUÉ EXISTE:
 *   El antipatrón previo en modales admin era construir el objeto
 *   `data.config` desde cero leyendo SOLO los inputs del DOM. Si el modal
 *   no exponía un campo (ej: `limites.maxApuesta`), ese campo desaparecía
 *   del objeto enviado y el backend lo borraba al persistir. Resultado
 *   real: editar chips dejaba `limites.maxApuesta=null` en 20/22 sorteos
 *   en producción, rompiendo el money-path del POS.
 *
 *   Este Borrador elimina la posibilidad estructuralmente: el "guardar"
 *   nunca arma un objeto desde inputs — siempre serializa la copia de
 *   trabajo completa, que conserva todos los campos que vinieron del
 *   backend más las ediciones del usuario. Lo no expuesto en el modal
 *   simplemente no se modifica y se reenvía intacto.
 *
 * USO TÍPICO EN UN MODAL:
 *   const borrador = BorradorDeEdicionDeEntidadConSnapshotOriginal
 *       .crearDesdeSnapshotOriginalRecibidoDelBackend(configCargada);
 *   borrador.hidratarConDefaultsDelContrato(CONTRATO_X);
 *
 *   // Lecturas/escrituras desde inputs del modal:
 *   borrador.escribirCampoEnPath('limites.maxApuesta', 75000);
 *   const valor = borrador.leerCampoEnPath('limites.maxApuesta');
 *
 *   // Validar antes de enviar (UX inmediato):
 *   const errores = borrador.validarContraContrato(CONTRATO_X);
 *   if (errores.length === 0) {
 *       await api.put(url, borrador.exportarCopiaDeTrabajoCompleta());
 *       const cambios = borrador.calcularKeysQueRealmenteCambiaronEntreSnapshotYCopia();
 *       toast(cambios.length === 0 ? 'Sin cambios' : 'Cambios: ' + cambios.join(', '));
 *   }
 *
 * SIN DEPENDENCIAS DE DOM. Es código puro, testeable en JVM/Node aislado.
 */

import {
    validarPayloadContraContrato,
    hidratarObjetoConDefaultsDelContrato,
} from './validar-payload-contra-contrato.js';

/**
 * Deep clone defensivo. JSON.parse/stringify es suficiente porque la
 * configuración de entidades es JSON-serializable por contrato (vive en
 * columnas TEXT en SQLite). No hay funciones, Dates, Maps ni Sets.
 *
 * Si en el futuro alguna entidad necesita tipos no-JSON, hay que cambiar
 * acá — pero entonces también rompe el contrato de "config en TEXT", lo
 * cual debería ser una conversación arquitectural antes que un parche.
 */
function clonarProfundamenteValorJsonSerializable(valor) {
    if (valor === null || valor === undefined) return valor;
    return JSON.parse(JSON.stringify(valor));
}

/**
 * Lectura por path con notación de puntos. Devuelve `undefined` si
 * cualquier eslabón intermedio no existe — nunca tira excepción, porque
 * el caller (input del modal) puede legítimamente preguntar por un campo
 * que aún no fue tocado.
 */
function leerValorEnObjetoPorPathConPuntos(objeto, path) {
    if (objeto === null || objeto === undefined) return undefined;
    const segmentos = path.split('.');
    let cursor = objeto;
    for (let i = 0; i < segmentos.length; i++) {
        if (cursor === null || cursor === undefined) return undefined;
        if (typeof cursor !== 'object') return undefined;
        cursor = cursor[segmentos[i]];
    }
    return cursor;
}

/**
 * Stringify canónico con keys ordenadas alfabéticamente en cada nivel.
 * Garantiza que dos objetos con el mismo contenido pero distinto orden de
 * claves produzcan el MISMO string. JSON.stringify nativo NO garantiza
 * orden — es bug-prone para comparaciones semánticas.
 *
 * Usado por `calcularKeysQueRealmenteCambiaronEntreSnapshotYCopia` para
 * detectar "no-cambio efectivo" en el toast post-save.
 */
function stringifyCanonico(valor) {
    if (valor === null || valor === undefined) return JSON.stringify(valor);
    if (typeof valor !== 'object') return JSON.stringify(valor);
    if (Array.isArray(valor)) return '[' + valor.map(stringifyCanonico).join(',') + ']';
    const keysOrdenadas = Object.keys(valor).sort();
    const partes = [];
    for (let i = 0; i < keysOrdenadas.length; i++) {
        const k = keysOrdenadas[i];
        partes.push(JSON.stringify(k) + ':' + stringifyCanonico(valor[k]));
    }
    return '{' + partes.join(',') + '}';
}

/**
 * Escritura por path con notación de puntos. Crea objetos intermedios
 * vacíos en el camino si no existen. Mutación in-place sobre `objeto`.
 *
 * No acepta paths que atraviesen arrays con índice (ej: `chips.0.color`).
 * Para arrays el caller debe leer el array completo, modificarlo, y
 * reescribirlo entero — esto es coherente con la regla de json-patch.js
 * "arrays se reemplazan completos, no se mergean elemento a elemento".
 */
function escribirValorEnObjetoPorPathConPuntos(objeto, path, valor) {
    const segmentos = path.split('.');
    let cursor = objeto;
    for (let i = 0; i < segmentos.length - 1; i++) {
        const seg = segmentos[i];
        if (
            cursor[seg] === null ||
            cursor[seg] === undefined ||
            typeof cursor[seg] !== 'object' ||
            Array.isArray(cursor[seg])
        ) {
            cursor[seg] = {};
        }
        cursor = cursor[seg];
    }
    cursor[segmentos[segmentos.length - 1]] = valor;
}

export class BorradorDeEdicionDeEntidadConSnapshotOriginal {
    /**
     * @param {object} snapshotOriginal — el objeto tal como vino del backend.
     *        Será deep-cloneado dos veces: una para conservar congelado
     *        como referencia inmutable, y otra como copia de trabajo
     *        editable.
     */
    constructor(snapshotOriginal) {
        this._snapshotOriginalCongelado = clonarProfundamenteValorJsonSerializable(snapshotOriginal) ?? {};
        this._copiaDeTrabajoEditable = clonarProfundamenteValorJsonSerializable(snapshotOriginal) ?? {};
        // Object.freeze recursivo costaría performance sin agregar seguridad
        // real (es shared/, no entra al hot path). El nombre del campo
        // (_snapshotOriginalCongelado) más la convención de prefijo "_"
        // ya comunican "no tocar".
    }

    /**
     * Constructor nombrado — preferido sobre `new` porque hace explícito
     * el origen del dato y permite validación futura sin romper callers.
     */
    static crearDesdeSnapshotOriginalRecibidoDelBackend(snapshotOriginal) {
        return new BorradorDeEdicionDeEntidadConSnapshotOriginal(snapshotOriginal);
    }

    leerCampoEnPath(path) {
        return leerValorEnObjetoPorPathConPuntos(this._copiaDeTrabajoEditable, path);
    }

    escribirCampoEnPath(path, valor) {
        escribirValorEnObjetoPorPathConPuntos(this._copiaDeTrabajoEditable, path, valor);
    }

    /**
     * Devuelve referencia interna a la copia de trabajo. PELIGROSO si el
     * caller la muta — de hecho la muta intencionalmente para flujos
     * legacy. Documentado como "exportar" para que sea explícito.
     *
     * Para lecturas seguras de campos individuales, usar leerCampoEnPath.
     */
    exportarCopiaDeTrabajoCompleta() {
        return this._copiaDeTrabajoEditable;
    }

    /**
     * Devuelve la lista de top-level keys que difieren CANÓNICAMENTE entre
     * el snapshot original y la copia de trabajo actual. Una key aparece
     * SOLO si su contenido cambió de verdad (no por orden de claves, no
     * por basura histórica equivalente).
     *
     * Considera keys presentes en uno y ausentes en el otro (alta, baja).
     * Considera keys con valores distintos.
     * Ignora keys con valores canónicamente idénticos.
     *
     * AGNÓSTICO de entidad: sirve para sorteos, nodos, pozos, dispositivos
     * o cualquier otra cosa cuya config sea un objeto JSON. La traducción
     * de keys a etiquetas humanas es responsabilidad del caller (cada
     * entidad tiene su vocabulario de dominio).
     *
     * USO TÍPICO en un toast de confirmación de guardado:
     *   const keys = borrador.calcularKeysQueRealmenteCambiaronEntreSnapshotYCopia();
     *   if (keys.length === 0) toast(`Sin cambios en "${nombre}"`);
     *   else toast(`Cambios en "${nombre}": ${keys.map(k => MIS_ETIQUETAS[k] || k).join(', ')}`);
     */
    calcularKeysQueRealmenteCambiaronEntreSnapshotYCopia() {
        const snapshot = this._snapshotOriginalCongelado || {};
        const copia = this._copiaDeTrabajoEditable || {};
        const todasLasKeys = new Set([...Object.keys(snapshot), ...Object.keys(copia)]);
        const cambiadas = [];
        for (const k of todasLasKeys) {
            if (stringifyCanonico(snapshot[k]) !== stringifyCanonico(copia[k])) {
                cambiadas.push(k);
            }
        }
        return cambiadas;
    }

    /**
     * Hidratación con defaults del contrato. Delega al SSOT
     * `hidratarObjetoConDefaultsDelContrato`. Aplica el mismo relleno tanto
     * a la copia de trabajo como al snapshot original — así el diff
     * posterior NO marca el relleno como "cambio del usuario": si el
     * usuario no toca nada, el toast dice "sin cambios" pero el backend
     * recibe el campo hidratado en el guardar (auto-reparación de DB legacy).
     */
    hidratarConDefaultsDelContrato(contrato) {
        hidratarObjetoConDefaultsDelContrato(this._copiaDeTrabajoEditable, contrato);
        hidratarObjetoConDefaultsDelContrato(this._snapshotOriginalCongelado, contrato);
    }

    /**
     * Validación contra contrato. Devuelve array de errores; vacío = OK.
     * Delega al SSOT `validarPayloadContraContrato` para que motor
     * (frontend) y validadores backend usen exactamente las mismas reglas.
     *
     * Defense in depth: el caller usa esto antes del save (rechazo temprano
     * con mensaje al usuario) Y el backend usa el mismo helper al recibir.
     */
    validarContraContrato(contrato) {
        return validarPayloadContraContrato(this._copiaDeTrabajoEditable, contrato);
    }
}

// CommonJS para tests Node sin transpilar.
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { BorradorDeEdicionDeEntidadConSnapshotOriginal };
}
